import {
    getBlob,
    getDownloadURL,
    ref,
    uploadBytesResumable,
} from 'firebase/storage'
import { storage } from '@/utils/firebase'

/**
 * Wrapper fino do Firebase Storage para o módulo Purchasing (Fase 5/5.0.2).
 * Upload em duas etapas: backend inicia (path controlado) → frontend envia
 * para storagePath → backend completa. Preview/download resolvem acesso em
 * runtime — nenhuma URL é persistida como identidade (§12).
 */

export const uploadAttachmentFile = (
    storagePath: string,
    file: File,
    onProgress?: (percent: number) => void
): Promise<string> => {
    const fileRef = ref(storage, storagePath)
    const task = uploadBytesResumable(fileRef, file)
    return new Promise((resolve, reject) => {
        task.on(
            'state_changed',
            (snapshot) => {
                const percent = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                )
                onProgress?.(percent)
            },
            (error) => reject(error),
            async () => {
                resolve(await getDownloadURL(fileRef))
            }
        )
    })
}

export const getAttachmentUrl = (storagePath: string): Promise<string> =>
    getDownloadURL(ref(storage, storagePath))

/**
 * §11 — download real usando o nome original.
 * Preferência: blob + objectURL (nome preservado de verdade).
 * Fallback: URL assinada com atributo download (pode cair em nova aba
 * dependendo do browser/token — documentado).
 */
export const downloadAttachmentFile = async (
    storagePath: string,
    originalFileName: string
): Promise<void> => {
    const fileRef = ref(storage, storagePath)
    try {
        const blob = await getBlob(fileRef)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = originalFileName
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
    } catch (err) {
        // Fallback seguro: abrir a URL assinada; o usuário baixa pelo navegador.
        console.warn('getBlob indisponível — usando URL assinada.', err)
        const url = await getDownloadURL(fileRef)
        window.open(url, '_blank', 'noopener')
    }
}
